import _ from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { Field, Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { FormApi } from 'final-form'

import { resolveFilterValue, resolveQueryString } from '../../utils/helpers'
import { parseGetParams } from '../../utils/routes'
import { composeValidators, emptyOrLength } from '../../utils/validation'
import { SelectField } from '../../containers/FormFields/SelectField'
import { Button } from '../Button'
import { FormControl } from '../FormControl'
import { Image } from '../Image'
import { Input } from '../Input'
import { Radio } from '../Radio'
import { RadioGroup } from '../RadioGroup'
import { Space } from '../Space'
import { Text } from '../Text'
import { Grid } from '../Grid'
import { ThemeWrapper, breakpoint, themeColor } from '../theme'

import { useSessionStorage, useViewportSize } from '../../hooks'

const SearchMenuContainer = styled(Space)<{ sticky?: boolean; mobile?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme, sticky, mobile }) => ` ${theme.space[sticky ? 2 : 5]} ${mobile ? '16px' : '80px'}`};
  overflow: hidden;
`

const Background = styled(Image)`
  display: block;
  position: absolute;
  right: 0;
  top: 0;
`

const SearchFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 1;
`
const SearchInputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 1;
  ${breakpoint.tablet(css`
    flex-direction: row;
  `)}
`
const ShowMoreWrapper = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.primary};
  justify-content: center;

  button: {
    padding: 3px 15px;
  }
`

const LabelsWrapper = styled(Space)`
  margin-bottom: 8px;
`
const LabelContainer = styled(Grid)`
  position: relative;
  grid-template-columns: repeat(auto-fit, minMax(10%, 40%));
`

const InputWrapper = styled(Space)`
  margin-bottom: 6px;
`
const CountryFilterInputWrapper = styled(InputWrapper)<{ marginRightSize?: string }>`
  background: ${themeColor('white')};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  font-weight: bold;
  ${({ marginRightSize }) =>
    breakpoint.tablet(css`
      margin-right: ${marginRightSize};
    `)}
`
const QueryInputWrapper = styled(InputWrapper)`
  flex: 1;
`
const ExternalLink = styled.a`
  font-weight: bold;
  line-height: initial;
  display: flex;
  align-items: center;
`

type FormValues = {
  query: string
  filter: string
  countryFilter?: string
}

export interface SearchMenuProps {
  searchWidgetDisplayMode?: 'WithFilters' | 'WithoutFilters'
  searchOptions: {
    label: string
    identifier: string
    placeholder?: string
    countries?: {
      label: string
      countryCode: string
    }[]
  }[]
  countryFilterEnabled: boolean
  defaultCountryFilter?: {
    label: string
    countryCode: string
  }
  otherCountriesFilter?: {
    label: string
    countryCode: string
  }[]
  externalSearchLink?: { text: string; url: string }
  sticky?: boolean
  mobile?: boolean
  collapseSearch?: () => void
  showMoreBtn?: {
    onClick: () => void
    text: React.ReactNode
  }
  onSearchClick: (params: { query: string; filter: string; countryFilter?: string }) => void
  queryString: string
  loggedIn: boolean
  hasBriqBulkImportRole: boolean
  showAsBulkImportButton: boolean | undefined
  showBulkImportLinkAboveSearchButton: boolean | undefined
  ImportHistoryLink: React.FunctionComponent
  showMulticolumnDropdown: boolean | undefined
  popupContainerClassName?: string
}

export const SearchMenu = ({
  searchWidgetDisplayMode,
  externalSearchLink,
  searchOptions,
  countryFilterEnabled,
  defaultCountryFilter,
  otherCountriesFilter,
  sticky,
  mobile,
  showMoreBtn,
  onSearchClick,
  queryString,
  loggedIn,
  hasBriqBulkImportRole,
  showAsBulkImportButton,
  showBulkImportLinkAboveSearchButton,
  ImportHistoryLink,
  showMulticolumnDropdown,
  popupContainerClassName,
  collapseSearch
}: SearchMenuProps) => {
  const { t } = useTranslation(['form', 'account', 'import'])

  const queryParams = parseGetParams(queryString)
  const [top, setTop] = useState<string>('0px')
  const [width, setWidth] = useState<string>('auto')

  const query = resolveQueryString(queryParams.query)
  const countriesFilter = defaultCountryFilter
    ? _.concat([defaultCountryFilter], otherCountriesFilter ?? [])
    : otherCountriesFilter ?? []

  const [selectedCountry, setSelectedCountry] = useSessionStorage(
    'regionalOfferingSelectedValue',
    defaultCountryFilter?.countryCode
  )

  const viewPortSize = useViewportSize()
  const isMobile = viewPortSize?.isMobile ?? false
  const sortedCountriesFilter = showMulticolumnDropdown
    ? _.sortBy(countriesFilter, o => o.label, ['asc'])
    : countriesFilter
  useEffect(() => {
    if (_.isString(queryParams.countryFilter)) {
      setSelectedCountry(queryParams.countryFilter)
    }
  }, [queryParams.countryFilter, setSelectedCountry])

  const countrySearchOptions = _.filter(
    searchOptions,
    so =>
      !countryFilterEnabled ||
      _.isEmpty(so.countries) ||
      _.isEmpty(selectedCountry) ||
      _.includes(
        _.map(so.countries, c => _.toLower(c.countryCode)),
        _.toLower(selectedCountry)
      )
  )

  const filterIdentifiers = _.map(countrySearchOptions, so => so.identifier)
  const firstFilterIdentifier = _.head(filterIdentifiers)

  const formRef: React.MutableRefObject<FormApi<FormValues, Partial<FormValues>> | null> = useRef(null)
  const selectedFilter = formRef?.current?.getState().values.filter
  const isSelectedFilterInvalid =
    formRef &&
    formRef.current &&
    selectedFilter &&
    filterIdentifiers.length > 0 &&
    !_.includes(filterIdentifiers, selectedFilter)

  useEffect(() => {
    isSelectedFilterInvalid && formRef?.current?.change('filter', firstFilterIdentifier)
  }, [isSelectedFilterInvalid, firstFilterIdentifier, formRef])

  const filter = resolveFilterValue(countrySearchOptions, queryParams.filter)

  const setCssOptions = (element: Element | null) => {
    if (element === null) return 0
    const top = viewPortSize?.isMobile
      ? '55px'
      : _.toInteger(element?.getBoundingClientRect().top) > 0
        ? `${_.toInteger(element?.getBoundingClientRect().top) + 48}px`
        : '0px'

    const width = viewPortSize?.isWide
      ? '86%'
      : viewPortSize?.isDesktop
        ? '79%'
        : _.toInteger(element?.getBoundingClientRect().width) > 0
          ? `${_.toInteger(element?.getBoundingClientRect().width)}px`
          : 'auto'

    setTop(top)
    setWidth(width)
  }

  return (
    <ThemeWrapper>
      <Form<FormValues>
        keepDirtyOnReinitialize // because form is sometimes automatically reinitialized when list of radio buttons with
        // search filters is changed - then we do not want to reinitialize fields from initialValues
        initialValues={{
          query,
          filter,
          countryFilter: !countryFilterEnabled
            ? undefined
            : _.isString(queryParams.countryFilter)
              ? queryParams.countryFilter
              : selectedCountry
        }}
        onSubmit={(values, form) => {
          if (!values.query) return
          collapseSearch && collapseSearch()
          onSearchClick(values)
          _.forEach(form.getRegisteredFields(), key => {
            form.resetFieldState(key as any)
          })
        }}
        render={({ handleSubmit, values, form }) => {
          formRef.current = form
          return (
            <form onSubmit={handleSubmit}>
              <SearchMenuContainer sticky={sticky} space={0} direction="vertical" mobile={mobile}>
                {!mobile && <Background type="headerBackground" />}
                <SearchFormWrapper className="qa--search-form-wrapper">
                  {!sticky && (
                    <LabelContainer mb={1} alignItems="center">
                      <LabelsWrapper>
                        <Text color="textPrimaryInverse">{t('form:SearchForBusiness')}</Text>
                        {externalSearchLink && (
                          <>
                            <Text color="textPrimaryInverse">|</Text>
                            <ExternalLink href={externalSearchLink.url} target="_blank">
                              <Text color="textPrimaryInverse" fontWeight="bold">
                                {externalSearchLink.text}
                              </Text>
                              <Image type="arrowRight2White" alt="arrow right" />
                            </ExternalLink>
                          </>
                        )}
                      </LabelsWrapper>
                      {loggedIn && hasBriqBulkImportRole && showBulkImportLinkAboveSearchButton && (
                        <LabelsWrapper>
                          <Text color="textPrimaryInverse">{t('import:MultipleSearch')}</Text>
                          <Text color="textPrimaryInverse">|</Text>
                          <ImportHistoryLink>
                            <Text color="textPrimaryInverse" fontWeight="bold">
                              {t('import:BulkImportLinkText')}
                            </Text>
                            <Image type="arrowRight2White" alt="arrow right" />
                          </ImportHistoryLink>
                        </LabelsWrapper>
                      )}
                    </LabelContainer>
                  )}
                  <SearchInputsWrapper>
                    {countryFilterEnabled && defaultCountryFilter && (
                      <CountryFilterInputWrapper
                        data-automation-id="countryFilterWrapper"
                        marginRightSize={showMulticolumnDropdown ? '20px' : '10px'}
                      >
                        <SelectField
                          name="countryFilter"
                          label={t('form:SearchCountry')}
                          labelProps={{
                            margin: '7px',
                            fontWeight: 700,
                            alignSelf: 'center',
                            color: 'primary'
                          }}
                          options={_.map(sortedCountriesFilter, x => ({ label: x.label, value: x.countryCode }))}
                          additionalProps={{
                            $colorVariant: 'secondary',
                            onChange: (value: any) => {
                              if (_.isString(value)) {
                                setSelectedCountry(value)
                              }
                            },
                            $orientation: 'horizontal',
                            ...(showMulticolumnDropdown
                              ? {
                                  SearchMode: isMobile ? 'MetaRow' : 'Inline',
                                  popupContainerClassName: popupContainerClassName,
                                  top: top,
                                  width: width,
                                  setCssOptions: (value: Element | null) => {
                                    setCssOptions(value)
                                  },
                                  numberOfColumns: viewPortSize?.isWide ? 7 : viewPortSize?.isDesktop ? 4 : 1,
                                  isMobile: viewPortSize?.isMobile
                                }
                              : {})
                          }}
                          direction="horizontal"
                          showMulticolumnDropdown={showMulticolumnDropdown}
                        />
                      </CountryFilterInputWrapper>
                    )}

                    <QueryInputWrapper space={2}>
                      <Field
                        name="query"
                        validate={composeValidators(emptyOrLength({ min: 3 }))}
                        render={p => (
                          <FormControl
                            {...p}
                            component={Input}
                            hideValidationMessage={true}
                            size="middle"
                            placeholder={
                              _.find(countrySearchOptions, x => x.identifier === values.filter)?.placeholder ||
                              _.head(searchOptions)?.placeholder
                            }
                            suffix={<Image type="search" alt="search" />}
                            data-automation-id="searchCompanyInput"
                          />
                        )}
                      />
                      <Button htmlType="submit" variant="secondary" data-automation-id="searchCompanyButton">
                        {t('search:Search')}
                      </Button>
                      {loggedIn && hasBriqBulkImportRole && showAsBulkImportButton && (
                        <ImportHistoryLink>
                          <Button icon={<Image type="arrowRight2Blue" alt="arrow right" />}>
                            {t('import:BulkImport')}
                          </Button>
                        </ImportHistoryLink>
                      )}
                    </QueryInputWrapper>
                  </SearchInputsWrapper>
                  {!_.isEmpty(countrySearchOptions) && (
                    <>
                      {searchWidgetDisplayMode === 'WithoutFilters' && (
                        <Field name="filter" type="hidden" component="input" />
                      )}
                      {searchWidgetDisplayMode === 'WithFilters' && (
                        <Field
                          name="filter"
                          type="text"
                          render={({ input }) => (
                            <RadioGroup {...input} inline>
                              {_.map(countrySearchOptions, ({ label, identifier }, i) => (
                                <Radio value={identifier} variant="secondary" color="textPrimaryInverse" key={i}>
                                  {label}
                                </Radio>
                              ))}
                            </RadioGroup>
                          )}
                        />
                      )}
                    </>
                  )}
                </SearchFormWrapper>
                {showMoreBtn && (
                  <ShowMoreWrapper>
                    <Button variant="transparent" padding="20px 20px 3px 20px" onClick={showMoreBtn.onClick}>
                      {showMoreBtn.text}
                    </Button>
                  </ShowMoreWrapper>
                )}
              </SearchMenuContainer>
            </form>
          )
        }}
      />
    </ThemeWrapper>
  )
}
